import { createFileRoute, Outlet } from "@tanstack/react-router";
import { CompactNoteForm } from "@/components/compact-note-form";
import { useQuery } from "@tanstack/react-query";
import { homeNotesQueryOptions } from "@/queries/home";
import { Button } from "@/components/ui/button";
import { Calendar, Star, Inbox } from "lucide-react";
import { useMemo } from "react";

import { Link } from "@tanstack/react-router";
import { useNoteCreation } from "@/hooks/useNoteCreation";
import { OverdueSection } from "@/components/home/overdue-section";
import { DueTodaySection } from "@/components/home/due-today-section";
import { RecentActivitySection } from "@/components/home/recent-activity-section";
import { UnauthenticatedState } from "@/components/home/unauthenticated-state";
import { EmptyState } from "@/components/home/empty-state";
import { OverdueFilterDropdown } from "@/components/today/overdue-filter-dropdown";
import { useOverdueFilter } from "@/hooks/use-overdue-filter";
import type { NoteTypes } from "db/types";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    const { user } = context;
    if (!user) {
      return { user: null };
    }
    return { user };
  },
  component: HomeIndex,
});

type Note = NoteTypes.SerializedNote;

function HomeIndex() {
  const { user } = Route.useRouteContext();
  const { data: homeNotes } = useQuery({
    ...homeNotesQueryOptions(user?.id),
    enabled: !!user?.id,
  });
  const { handleNoteCreated, isPending: isCreateNotePending } = useNoteCreation(
    user?.id || "",
  );

  const {
    excludedTypes,
    toggleType,
    isTypeIncluded,
    hasActiveFilters,
    allIncluded,
    includeAll,
    excludeAll,
  } = useOverdueFilter();

  if (!user?.id) {
    return (
      <>
        <header className="flex h-14 md:h-16 shrink-0 items-center gap-3 px-4 md:px-6 overflow-x-hidden">
          <div className="flex-1">
            <h1 className="text-lg md:text-xl font-semibold tracking-tight text-foreground flex items-center gap-4">
              <Link to="/">Home</Link>
            </h1>
          </div>
        </header>
        <UnauthenticatedState />
      </>
    );
  }

  const {
    overdueNotes = [],
    dueTodayNotes = [],
    recentlyAdded = [],
  } = user?.id && homeNotes
    ? homeNotes
    : { overdueNotes: [], dueTodayNotes: [], recentlyAdded: [] };

  const filteredOverdueNotes = useMemo(() => {
    return overdueNotes.filter((note: Note) => isTypeIncluded(note.type || ""));
  }, [overdueNotes, isTypeIncluded]);

  const filteredDueTodayNotes = useMemo(() => {
    return dueTodayNotes.filter((note: Note) =>
      isTypeIncluded(note.type || ""),
    );
  }, [dueTodayNotes, isTypeIncluded]);

  const hasContent =
    filteredOverdueNotes.length > 0 ||
    filteredDueTodayNotes.length > 0 ||
    recentlyAdded.length > 0;

  return (
    <>
      <>
        <div className="animate-fade-in max-w-4xl mx-auto w-full mb-6 md:mb-8 py-3 md:py-4 px-4">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
            Notebook
          </h1>
          <CompactNoteForm
            onSubmit={handleNoteCreated}
            isSubmitting={isCreateNotePending}
          />
        </div>

        <div className="flex-1 flex flex-col gap-6 md:gap-8 w-full max-w-4xl mx-auto min-h-screen px-4 overflow-x-hidden">
          {/* Quick Navigation */}
          <div className="flex items-center gap-2 flex-wrap font-display">
            <Button variant="outline" size="sm" asChild>
              <Link to="/today" className="gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                Today
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/upcoming" className="gap-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                Upcoming
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/inbox" className="gap-2">
                <Inbox className="h-4 w-4 text-purple-500" />
                Inbox
              </Link>
            </Button>
            <div className="ml-auto">
              <OverdueFilterDropdown
                excludedTypes={excludedTypes}
                onToggleType={toggleType}
                isTypeIncluded={isTypeIncluded}
                hasActiveFilters={hasActiveFilters}
                allIncluded={allIncluded}
                onIncludeAll={includeAll}
                onExcludeAll={excludeAll}
              />
            </div>
          </div>

          {/* Home Sections */}
          {user?.id && (
            <>
              <OverdueSection notes={filteredOverdueNotes} />
              <DueTodaySection notes={filteredDueTodayNotes} />
              <RecentActivitySection notes={recentlyAdded} />
            </>
          )}

          {/* Empty State */}
          {!hasContent && <EmptyState />}
        </div>
      </>

      <Outlet />
    </>
  );
}
